import React, { useState } from 'react';
import { StateManager } from '../managers/StateManager';
import { COLORS, WIRE_DEFAULTS } from '../utils/constants';

interface WireEditModalProps {
  stateManager: StateManager;
  wireId: string;
  onClose: () => void;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '4px',
  fontSize: '13px',
  color: '#636e72'
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '14px',
  boxSizing: 'border-box'
};

/** Compact colour picker so the two swatches sit inline rather than filling the row. */
const colorInputStyle: React.CSSProperties = {
  width: '44px',
  height: '32px',
  padding: '2px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  boxSizing: 'border-box',
  cursor: 'pointer'
};

/** Common conductor cross-sections (mm²) offered as quick presets. */
const GAUGE_PRESETS = [0.35, 0.5, 0.75, 1.5, 2.5, 4];

/**
 * Form dialog for a wire's editable properties. Name and note are required-ish
 * (name falls back to a placeholder); color, gauge, wire type and an optional
 * stripe are left to the defaults unless the user sets them explicitly.
 */
export const WireEditModal: React.FC<WireEditModalProps> = ({ stateManager, wireId, onClose }) => {
  const wire = stateManager.getState().wires.find(w => w.id === wireId);

  const initialColor = wire?.color ?? WIRE_DEFAULTS.COLOR;
  const [name, setName] = useState(wire?.name ?? '');
  const [note, setNote] = useState(wire?.note ?? '');
  const [color, setColor] = useState(initialColor);
  // The second colour mirrors the primary until the user picks a different one,
  // at which point the wire is drawn striped.
  const [stripeColor, setStripeColor] = useState(wire?.stripeColor ?? initialColor);
  const [gaugeText, setGaugeText] = useState(wire?.gauge != null ? String(wire.gauge) : '');
  const [wireType, setWireType] = useState(wire?.wireType ?? '');

  if (!wire) {
    return null;
  }

  const striped = stripeColor !== color;

  // Selecting the primary colour copies it to the second colour while they still
  // match, so a single-colour wire stays single until the second colour is changed.
  const handlePrimaryColor = (value: string) => {
    if (!striped) {
      setStripeColor(value);
    }
    setColor(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const gauge = gaugeText.trim() === '' ? undefined : Number(gaugeText);
    stateManager.updateWire(wireId, {
      name: name.trim() || 'Unnamed Wire',
      note: note.trim(),
      color,
      stripeColor: striped ? stripeColor : undefined,
      gauge: gauge != null && Number.isFinite(gauge) ? gauge : undefined,
      wireType: wireType.trim() === '' ? undefined : wireType.trim()
    });
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 20000
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          minWidth: '360px'
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onClose();
          }
        }}
      >
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#2d3436' }}>Edit Wire</h3>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus style={inputStyle} />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Note</label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} style={inputStyle} />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Color{striped ? ' (striped)' : ''}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="color"
                value={color}
                onChange={(e) => handlePrimaryColor(e.target.value)}
                title="Primary colour"
                style={colorInputStyle}
              />
              <input
                type="color"
                value={stripeColor}
                onChange={(e) => setStripeColor(e.target.value)}
                title="Second colour (match the primary for a single colour)"
                style={colorInputStyle}
              />
              {striped && (
                <button
                  type="button"
                  onClick={() => setStripeColor(color)}
                  style={{ padding: '4px 10px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: 'white', color: '#636e72', cursor: 'pointer', fontSize: '13px' }}
                >
                  Single
                </button>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Gauge (mm²)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
              {GAUGE_PRESETS.map((preset) => {
                const active = gaugeText.trim() !== '' && Number(gaugeText) === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setGaugeText(String(preset))}
                    style={{
                      padding: '5px 10px',
                      border: `1px solid ${active ? COLORS.ACTION : '#ddd'}`,
                      borderRadius: '4px',
                      backgroundColor: active ? '#e6eef0' : 'white',
                      color: active ? COLORS.ACTION : '#2d3436',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    {preset}
                  </button>
                );
              })}
            </div>
            <input
              type="number"
              min="0"
              step="0.05"
              value={gaugeText}
              onChange={(e) => setGaugeText(e.target.value)}
              placeholder={String(WIRE_DEFAULTS.GAUGE)}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Wire type</label>
            <input
              type="text"
              value={wireType}
              onChange={(e) => setWireType(e.target.value)}
              placeholder={WIRE_DEFAULTS.TYPE}
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: '#636e72',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: COLORS.ACTION,
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
